import { FC, useMemo, useState } from 'react';
import { Box, Typography, Stack, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
} from 'date-fns';

import { StepOverview } from './StepOverview';


interface Props {
  program: Programs.Program;
  stepEntries: Programs.StepEntry[];
  onDayClick?: (entries: Programs.StepEntry[]) => void;
  onEntryClick?: (entry: Programs.StepEntry) => void;
}

export const JourneyCalendar: FC<Props> = ({ program, stepEntries, onDayClick, onEntryClick }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const entriesByDate = useMemo(() => {
    const grouped: Record<string, Programs.StepEntry[]> = {};
    stepEntries.forEach(entry => {
      const date = format(new Date(entry.completedAt), 'yyyy-MM-dd');
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(entry);
    });
    return grouped;
  }, [stepEntries]);

  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const weeks = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(monthEnd);

    const rows: Date[][] = [];
    let day = calStart;
    while (day <= calEnd) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(day);
        day = addDays(day, 1);
      }
      rows.push(week);
    }
    return rows;
  }, [currentMonth]);

  const handleDayClick = (day: Date) => {
    const key = format(day, 'yyyy-MM-dd');
    const entries = entriesByDate[key];
    if (entries) {
      setSelectedDay(selectedDay === key ? null : key);
      onDayClick?.(entries);
    }
  };

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <IconButton size="small" onClick={() => setCurrentMonth(prev => subMonths(prev, 1))} color="inherit">
          <ChevronLeft/>
        </IconButton>
        <Typography variant="subtitle1" fontSize={14}>
          {format(currentMonth, 'MMMM yyyy')}
        </Typography>
        <IconButton size="small" onClick={() => setCurrentMonth(prev => addMonths(prev, 1))} color="inherit">
          <ChevronRight/>
        </IconButton>
      </Stack>

      <Box display="grid" gridTemplateColumns="repeat(7, 1fr)" rowGap={1.5}>
        {weekDays.map((day, i) => (
          <Typography
            key={i}
            variant="caption"
            textAlign="center"
            color="text.secondary"
            fontSize={12}
            pb={1}
          >
            {day}
          </Typography>
        ))}

        {weeks.flat().map((day, idx) => {
          const key = format(day, 'yyyy-MM-dd');
          const hasEntries = !!entriesByDate[key];
          const inMonth = isSameMonth(day, currentMonth);
          const today = isToday(day);
          const isSelected = selectedDay === key;

          return (
            <Box
              key={idx}
              onClick={() => handleDayClick(day)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 0.5,
                cursor: hasEntries ? 'pointer' : 'default',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: today ? '1.5px solid' : 'none',
                  borderColor: today ? 'primary.main' : 'transparent',
                  backgroundColor: isSelected ? 'rgba(212, 162, 84, 0.15)' : 'transparent',
                }}
              >
                <Typography
                  variant="body2"
                  color={!inMonth ? 'rgba(245, 240, 232, 0.2)' : today ? 'primary.main' : 'text.primary'}
                  fontWeight={today || hasEntries ? 700 : 400}
                  fontSize={14}
                >
                  {format(day, 'd')}
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  mt: 0.25,
                  backgroundColor: hasEntries && inMonth ? 'primary.main' : 'transparent',
                }}
              />
            </Box>
          );
        })}
      </Box>

      {selectedDay && entriesByDate[selectedDay] && (
        <StepOverview
          program={program}
          stepEntry={entriesByDate[selectedDay][0]}
          onCloseClick={() => handleDayClick(new Date(selectedDay))}
          onViewClick={() => onEntryClick?.(entriesByDate[selectedDay][0])}
        />
      )}
    </Stack>
  );
};
